/**
 * Copyright 2023-2025 Highway9 Networks Inc.
 */
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState, nanoid, PayloadAction } from "@reduxjs/toolkit";
import { RootState, store } from "..";
import { siteService } from "../../services/site-service";
import { RadioGroup } from "../../types/radioGroup";
import Radio, { RadioX } from "../../types/radio";
import { fetchRadios, radioData } from "./radioSlice";
import { Types } from "../../constants/types";
import { Box, Circle, convertSvgToUrl, MarkerData, RadioPin } from "@h9/ui-lib";
import { BAICELLS, CARRIER_MODE, COLOR, HIGHWAY9, MIN_SITE_RADIUS, RADIO_TRIGGER_RADIUS } from "~/constants";
import { groupBy } from "~/helpers/utils";
import { siteEdgesHave5G } from "~/helpers/siteEdgeHelpers";
import { checkStatus, getBandChannels } from "~/views/radios/radioHelper";
import { createSocketPatchReducer } from "../events/socketPatch";
import { getRadioIconType } from "~/helpers/radioHelper";
import { PlanCoods } from "~/components/shared/d3/FloorPlan";
import { geoDistance } from "~/helpers/geoDistance";
import { defaultSite } from "~/types/site";
import { siteEntities } from "./siteSlice";
import { edgeEdgesWithPool, edgeEntities } from "./edgeSlice";

type mappingID_with_APCount = {
  [key: string]: number;
};
type mappingStatus = {
  [key: string]: string;
};

type initState = {
  open: boolean;
  edit: boolean;
  selectedId: string | null;
  data: EntityState<RadioGroup>;
  status: string;
  channelPlanOpen: boolean;

  // from radio groups
  selectedIDs: string[];
  radioMappingIds?: mappingID_with_APCount;
  connectedRadioMappingIds?: mappingID_with_APCount;
  radioMappingStatus?: mappingStatus;

  // from edges
  showAdvanceEdges: boolean;
};

const radioGroupAdapter = createEntityAdapter<RadioGroup>({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});


export const initialState: initState = {
  open: false,
  edit: false,
  selectedId: null,
  status: "idle",
  data: radioGroupAdapter.getInitialState(),
  channelPlanOpen: false,

  // from radio groups
  selectedIDs: [Types.all],
  radioMappingIds: {},
  connectedRadioMappingIds: {},
  radioMappingStatus: {},
  showAdvanceEdges: false,
};

export const fetchRadioGroups = createAsyncThunk("radioGroup/fetchRadioGroups", async (query: { timestamp?: number }, thunk) => {
  const timestamp = query?.timestamp;
  const queryString = timestamp ? { timestamp } : {};

  // Store API groups only — map pins / plmn / nested radios are derived via selectors.
  // Still warm the radio cache so enrichment selectors have data when maps mount.
  const _radioGroups = await siteService.getRadioGroups(queryString);
  const radios = radioData(thunk.getState() as RootState);
  if (!radios?.length) {
    await thunk.dispatch(fetchRadios({ timestamp }));
  }
  return _radioGroups;
});

export const radioGroupSlice = createSlice({
  name: Types.radioGroup,
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setEdit: (state, action: PayloadAction<boolean>) => {
      state.edit = action.payload;
    },
    /** Store selection identity only; resolve the entity via selectors. */
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setData: (state, action: PayloadAction<RadioGroup[]>) => {
      radioGroupAdapter.setAll(state.data, action.payload);
    },
    updateData: (state, action: PayloadAction<RadioGroup>) => {
      radioGroupAdapter.upsertOne(state.data, action.payload);
    },

    setChannelPlanOpen: (state, action: PayloadAction<boolean>) => {
      state.channelPlanOpen = action.payload;
    },

    setSelectedIds: (state, action: PayloadAction<string[]>) => {
      state.selectedIDs = action.payload;
    },
    /** Atomic write for count/status maps so sync only notifies subscribers once. */
    setRadioGroupMappings: (
      state,
      action: PayloadAction<{
        radioMappingIds: mappingID_with_APCount;
        connectedRadioMappingIds: mappingID_with_APCount;
        radioMappingStatus: mappingStatus;
      }>,
    ) => {
      state.radioMappingIds = action.payload.radioMappingIds;
      state.connectedRadioMappingIds = action.payload.connectedRadioMappingIds;
      state.radioMappingStatus = action.payload.radioMappingStatus;
    },

    setShowAdvanceEdges: (state, action: PayloadAction<boolean>) => {
      state.showAdvanceEdges = action.payload;
    },

    clearEntityFloorPlanImage: (state, action: PayloadAction<string>) => {
      const group = state.data.entities[action.payload];
      if (group?.plan?.calibrationPoints.floorPlan) {
        delete group.plan.calibrationPoints.floorPlan.imageData;
        delete group.plan.calibrationPoints.floorPlan.imageId;
        delete group.plan.calibrationPoints.floorPlan.imageName;
      }
    },
    applySocketPatches: createSocketPatchReducer(radioGroupAdapter, (s) => s.data),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRadioGroups.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchRadioGroups.fulfilled, (state, action) => {
        radioGroupAdapter.setAll(state.data, action.payload);
        if (state.selectedId && !state.data.entities[state.selectedId]) {
          state.selectedId = null;
        }
        state.status = "idle";
      })
      .addCase(fetchRadioGroups.rejected, (state) => {
        state.status = "error";
      });
  },
});

export default radioGroupSlice.reducer;
export const radioGroupActions = radioGroupSlice.actions;

const radioGroupSelectors = radioGroupAdapter.getSelectors((state: RootState) => state.radioGroup.data);

/** Selected group identity only — no map-pin enrichment. */
const radioGroupStateRaw = createSelector(
  [(state: RootState) => state.radioGroup.selectedId, radioGroupSelectors.selectEntities],
  (selectedId, entities): RadioGroup | null => (selectedId ? entities[selectedId] ?? null : null),
);
export const radioGroupOpen = (state: RootState) => state.radioGroup.open;
export const radioGroupEdit = (state: RootState) => state.radioGroup.edit;
export const radioGroupData = radioGroupSelectors.selectAll;
export const radioGroupEntities = radioGroupSelectors.selectEntities;
export const radioChannelPlanOpen = (state: RootState) => state.radioGroup.channelPlanOpen;

export const radioGroupSelected = (state: RootState) => state.radioGroup.selectedIDs;
export const radioGroupMappingIds = (state: RootState) => state.radioGroup.radioMappingIds;
export const radioGroupConnectedMappingIDs = (state: RootState) => state.radioGroup.connectedRadioMappingIds;
export const radioGroupMappingStatus = (state: RootState) => state.radioGroup.radioMappingStatus;
export const radioGroupShowAdvanceEdges = (state: RootState) => state.radioGroup.showAdvanceEdges;
export const radioGroupLoading = (state: RootState) => state.radioGroup.status === "loading";

export const radioGroupHas5GEdge = createSelector(
  radioGroupStateRaw,
  edgeEntities,
  edgeEdgesWithPool,
  siteEntities,
  (currentGroup, edgesById, edgesWithPools, siteEntitiesMap) => {
    const radioGroupSiteId = currentGroup?.radioSite?.id;
    const site = radioGroupSiteId ? siteEntitiesMap[radioGroupSiteId] : undefined;
    return siteEdgesHave5G(site?.edges, edgesById, edgesWithPools);
  },
);

/** Radios bucketed by radioGroup.id — shared input for on-demand enrichment. */
export const selectRadiosByGroupId = createSelector([radioData], (radios) => {
  const byGroup: Record<string, Radio[]> = {};
  for (const radio of radios) {
    const gid = radio.radioGroup?.id;
    if (!gid) continue;
    const bucket = byGroup[gid];
    if (bucket) bucket.push(radio);
    else byGroup[gid] = [radio];
  }
  return byGroup;
});

export function derivePlmnAndVendor(radios: Radio[]): { _plmnList: string[]; _vendorList: string[] } {
  const plmnList: string[] = [];
  const vendorList: string[] = [];
  for (const radio of radios) {
    if (radio.runtimeInfo?.plmnList) {
      plmnList.push(...radio.runtimeInfo.plmnList);
    }
    if (radio.runtimeInfo?.vendorType) {
      vendorList.push(radio.runtimeInfo.vendorType === BAICELLS ? HIGHWAY9 : radio.runtimeInfo.vendorType);
    }
  }
  return {
    _plmnList: Array.from(new Set(plmnList)),
    _vendorList: Array.from(new Set(vendorList)),
  };
}

/** Cheap PLMN/vendor maps for KPI filters — no pin generation. */
export const selectPlmnVendorByGroupId = createSelector([selectRadiosByGroupId], (radiosByGroup) => {
  const result: Record<string, { _plmnList: string[]; _vendorList: string[] }> = {};
  for (const [id, radios] of Object.entries(radiosByGroup)) {
    result[id] = derivePlmnAndVendor(radios);
  }
  return result;
});

/** Enrich one group with nested radios, map pins, floorplan coords, plmn/vendor. */
export function enrichRadioGroup(grp: RadioGroup, radios: Radio[]): RadioGroup {
  const coods: MarkerData[] = [];
  const planCoods: PlanCoods[] = [];

  let fallbackIndex = 0;
  const radiosWithoutFloorPlan = radios.filter((radio) => !radio.floorPlan?.xOffset || !radio.floorPlan?.yOffset);
  const baseMultiplier = radios.length > 1 ? 55 - Math.min(50, radios.length * 2) : 55;
  const offsetMultiplier =
    radiosWithoutFloorPlan.length > 1 ? baseMultiplier + 200 / radiosWithoutFloorPlan.length : baseMultiplier;

  for (const radio of radios) {
    if (!radio) continue;

    const marker = generateRadioPin(radio, grp);

    const planCood: PlanCoods = {
      x: radio.floorPlan?.xOffset ?? 500 + fallbackIndex * offsetMultiplier * (-1) ** fallbackIndex,
      y: radio.floorPlan?.yOffset ?? 500 + fallbackIndex * offsetMultiplier * (-1) ** fallbackIndex,
      id: radio.id ?? nanoid(),
      icon: marker?.icon,
      title: radio.name,
    };

    if (!radio.floorPlan?.xOffset || !radio.floorPlan?.yOffset) {
      fallbackIndex++;
    }

    if (marker) coods.push(marker);
    planCoods.push(planCood);
  }

  const { _plmnList, _vendorList } = derivePlmnAndVendor(radios);

  return {
    ...grp,
    radios,
    radioCoordinates: coods,
    radioFloorPlanCoods: planCoods,
    _plmnList,
    _vendorList,
  };
}

/** Selected group with map/KPI enrichment (pins only for the selected id). */
export const radioGroupState = createSelector(
  [radioGroupStateRaw, selectRadiosByGroupId],
  (group, radiosByGroup): RadioGroup | null => {
    if (!group?.id) return group;
    return enrichRadioGroup(group, radiosByGroup[group.id] ?? []);
  },
);

const enrichedRadioGroupByIdSelectors = new Map<string, (state: RootState) => RadioGroup | null>();

/** Enriched group by id — memoized per id so callers don't rebuild pins on unrelated store ticks. */
export const radioGroupById = (id: string) => {
  let selector = enrichedRadioGroupByIdSelectors.get(id);
  if (!selector) {
    selector = createSelector(
      [radioGroupEntities, selectRadiosByGroupId],
      (entities, radiosByGroup): RadioGroup | null => {
        const grp = entities[id];
        if (!grp) return null;
        return enrichRadioGroup(grp, radiosByGroup[id] ?? []);
      },
    );
    enrichedRadioGroupByIdSelectors.set(id, selector);
  }
  return selector;
};

/** Factory: enrich a single group by id (use with useMemo(() => makeSelect…(), [])). */
export const makeSelectEnrichedRadioGroup = () =>
  createSelector(
    [radioGroupEntities, selectRadiosByGroupId, (_state: RootState, groupId: string) => groupId],
    (entities, radiosByGroup, groupId): RadioGroup | undefined => {
      if (!groupId) return undefined;
      const grp = entities[groupId];
      if (!grp) return undefined;
      return enrichRadioGroup(grp, radiosByGroup[groupId] ?? []);
    },
  );

/** Factory: enrich groups for one site (SiteMap / site forms). */
export const makeSelectEnrichedRadioGroupsForSite = () =>
  createSelector(
    [radioGroupData, selectRadiosByGroupId, (_state: RootState, siteId: string | undefined) => siteId],
    (groups, radiosByGroup, siteId): RadioGroup[] => {
      if (!siteId) return [];
      return groups
        .filter((g) => g.radioSite?.id === siteId)
        .map((g) => enrichRadioGroup(g, g.id ? radiosByGroup[g.id] ?? [] : []));
    },
  );

function mapsShallowEqual(
  a: Record<string, string | number> | undefined,
  b: Record<string, string | number> | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every((key) => a[key] === b[key]);
}

export function syncRadioStatusAndConnectedWithGroups(data: RadioX[]) {
  const groupedRows = groupBy(data, "radioGroup.id");
  const radioMappingIds: mappingID_with_APCount = {};
  const connectedRadioMappingIds: mappingID_with_APCount = {};
  const radioMappingStatus: mappingStatus = {};

  for (const key of Object.keys(groupedRows)) {
    const radios = groupedRows[key];
    const statusColors = radios.map((radio) => radio.statusColor);
    radioMappingStatus[key] = checkStatus(statusColors);
    connectedRadioMappingIds[key] = statusColors.filter((item) => item === COLOR.GREEN).length;
    radioMappingIds[key] = radios.length;
  }

  const current = store.getState().radioGroup;
  if (
    mapsShallowEqual(current.radioMappingIds, radioMappingIds) &&
    mapsShallowEqual(current.connectedRadioMappingIds, connectedRadioMappingIds) &&
    mapsShallowEqual(current.radioMappingStatus, radioMappingStatus)
  ) {
    return;
  }

  store.dispatch(
    radioGroupActions.setRadioGroupMappings({
      radioMappingIds,
      connectedRadioMappingIds,
      radioMappingStatus,
    }),
  );
}


export function getRadioCircles(grp: RadioGroup, radios: Radio[]) {
  const circles: Circle[] = [];

      // if zone type is indoor, then add a circle
      if (grp.zoneType?.toLowerCase() === "indoor" && radios.length > 1) {

        // get the center of the circle from the radios
        let count = 0;
        const centroid = radios.reduce((acc, radio) => {
          if (!radio.location?.latitude || !radio.location?.longitude) return acc;
          count++;
          return {
            lat: acc.lat + radio.location?.latitude,
            lng: acc.lng + radio.location?.longitude,
          }
        }, { lat: 0, lng: 0 });
        centroid.lat /= count;
        centroid.lng /= count;
        // calculate the max distance of the circle from the center to the farthest radio
        let maxDistance = MIN_SITE_RADIUS;
        for (const radio of radios) {
          // if the radio is not located, then skip it
          if (radio.location?.latitude === 0 && radio.location?.longitude === 0) continue;

          const distance = geoDistance(centroid?.lat ?? 0, centroid?.lng ?? 0, radio.location?.latitude ?? 0, radio.location?.longitude ?? 0);
          maxDistance = Math.max(maxDistance, distance);
        }

        const data : Circle = {
          center: {
            lat: centroid?.lat ?? 0,
            lng: centroid?.lng ?? 0,
        },
        radius: maxDistance + RADIO_TRIGGER_RADIUS,
        color: '#226a58',
        count: radios.length,
        title: grp.name,
        // Tooltip lists radio names, which no other compared field encodes
        tooltipKey: radios.map((radio) => radio.name).join('|'),
        tooltipRender: (circle) => {
          // const areaInFeet = (Math.PI * (circle.radius ** 2)) * 10.7639;
          // const { value, notation } = formatCompactNumberInParts(areaInFeet);
          return <Box p={1}>
            <Box style={{ textTransform: "capitalize", fontSize: '12px', fontWeight: 'bold' }}>
              {circle.title}
              <br />
              <span style={{color: '#ccc', fontSize: '12px'}}>Count - {circle.count} APs</span>
            </Box>
            <div style={{ marginLeft: 10}}>
              {radios.map((radio) => {
                return <div key={radio.id}>
                  <span style={{ fontSize: '12px'}}>
                    &bull; {radio.name}
                  </span>
                </div>
              })}
            </div>
          </Box>
          }
        }

        circles.push(data);
      }
      return circles;
}

// Radio pins are rebuilt for every radio on every realtime refresh. Serializing the SVG
// each time (renderToString + XMLSerializer) is pure churn when the pin's inputs are
// unchanged, so cache the resulting icon URL keyed by its exact visual inputs. Bounded
// FIFO so the cache itself can't grow without limit (MCS-3969).
const radioPinIconCache = new Map<string, string>();
const RADIO_PIN_CACHE_MAX = 3000;
function cachedIcon(key: string, build: () => string): string {
  let url = radioPinIconCache.get(key);
  if (url === undefined) {
    url = build();
    radioPinIconCache.set(key, url);
    if (radioPinIconCache.size > RADIO_PIN_CACHE_MAX) {
      const oldest = radioPinIconCache.keys().next().value;
      if (oldest !== undefined) radioPinIconCache.delete(oldest);
    }
  }
  return url;
}

export function generateRadioPin(radio: Radio, grp?: RadioGroup) {

  if (!radio) return;


  const zoneType = getRadioIconType(radio);

  const location = {
    lat: radio?.location?.latitude ?? grp?.plan?.calibrationPoints?.map?.points[0]?.latitude ?? 0,
    lng: radio?.location?.longitude ?? grp?.plan?.calibrationPoints?.map?.points[0]?.longitude ?? 0,
  };

  // if the location is 0, 0
  //  then use the radio group center as the location of the radio
  //  if the radio group is not found, then use the default site center as the location of the radio
  if (location.lat === 0 && location.lng === 0) {
    location.lat = grp?.plan?.calibrationPoints?.map?.center?.latitude ?? defaultSite?.location?.latitude ?? 0;
    location.lng = grp?.plan?.calibrationPoints?.map?.center?.longitude ?? defaultSite?.location?.longitude ?? 0;
  }

  const isBorderRadio = radio.isBorderRadio ?? false;
  let icon: string;

  if (radio.carrierMode !== CARRIER_MODE.SINGLE_CARRIER) {
    const sectors = radio.cellParameters?.map((cell, index) => {
      const runtimeCell = radio.runtimeInfo?.cellParameters?.[index];
      const bandChannels = getBandChannels((runtimeCell?.bandwidth ?? cell.bandwidth)!, (cell.band)!);
      const channel = bandChannels.find((ch) => ch.earfcn === (runtimeCell?.earfcn ?? cell.earfcn?.[0]));
      const EIRP = (runtimeCell?.power ?? cell.power ?? 0) + (cell.gain ?? 0);
      return {
        angle: cell.azimuth ?? 0,
        beamwidth: cell.beamwidth ?? 0,
        gain: radio.carrierMode === CARRIER_MODE.DUAL_CARRIER ? EIRP : index === 0 ? EIRP : EIRP / 4,
        channelColor: channel?.color.slice(0, 7) ?? "#D3D3D3",
      };
    }) ?? [];
    icon = cachedIcon(
      `m|${zoneType}|${isBorderRadio}|${radio.technology}|${radio.runtimeInfo?.overallHealth ?? "UNKNOWN"}|${JSON.stringify(sectors)}`,
      () =>
        convertSvgToUrl(
          <RadioPin
            type={zoneType}
            parameters={{
              sectors,
              health: radio.runtimeInfo?.overallHealth ?? "UNKNOWN",
              network: radio.technology,
            }}
            isBorderRadio={isBorderRadio}
          />,
        ),
    );
  } else {
    const bandChannels = getBandChannels(
      (radio.runtimeInfo?.cellParameters?.[0].bandwidth ?? radio.cellParameters?.[0].bandwidth)!,
      radio.cellParameters?.[0].band,
    );
    const channel = bandChannels.find(
      (ch) => ch.earfcn === (radio.runtimeInfo?.cellParameters?.[0].earfcn ?? radio.cellParameters?.[0].earfcn?.[0]),
    );
    const singleParams = {
      angle: radio.cellParameters?.[0].azimuth ?? 0,
      beamwidth: radio.cellParameters?.[0].beamwidth ?? 0,
      gain:
        (radio.runtimeInfo?.cellParameters?.[0].power ?? radio.cellParameters?.[0].power ?? 0) +
        (radio.cellParameters?.[0].gain ?? 0),
      health: radio.runtimeInfo?.overallHealth ?? "UNKNOWN",
      channelColor: channel?.color.slice(0, 7) ?? "#D3D3D3",
      network: radio.technology,
    };
    icon = cachedIcon(`s|${zoneType}|${isBorderRadio}|${JSON.stringify(singleParams)}`, () =>
      convertSvgToUrl(<RadioPin type={zoneType} parameters={singleParams} isBorderRadio={isBorderRadio} />),
    );
  }

  const marker : MarkerData = {
    id: radio.id ?? nanoid(),
    location,
    title: radio.name,
    icon,
    radioType: zoneType,
    metadata: radio.cellParameters?.map((cell, index) => {
      const runtimeCell = radio.runtimeInfo?.cellParameters?.[index];
      return({
      "EARFCN": runtimeCell?.earfcn ?? cell.earfcn?.[0] ?? 0,
      "PCI":  cell.pci ?? 0,
      "Azi°": `${cell.azimuth ?? 0}°`,
      "BmW": `${cell.beamwidth ?? 0}°`,
      "Gain": `${cell.gain ?? 0}dBi`,
      "Power": `${runtimeCell?.power ?? cell.power ?? 0}dBm`,
      "Height": `${cell.altitude?.toFixed(2) ?? 0}m`,
    })})
  }

  return marker;
}
